// =====================================================================
// src/modulos/painel.js — Painel de tempo real: as TVs do hotel
//
// Uma tela para ser lida de longe, no layout "Liberação de Quartos"
// (esboço aprovado em 15/09/2026): o alerta grande (chegadas de hoje sem
// quarto liberado), quatro medidores, a grade dos andares com os quartos
// nas cores, a prioridade da governança (ou as chegadas por hora, na
// recepção), o detalhe de um quarto ao tocar, e o movimento ao vivo.
//
// Duas VISTAS (#painel?vista=recepcao; sem parâmetro, quem vê hóspedes
// abre na da recepção — 0024: o painel da recepção é nos computadores dos
// recepcionistas, não numa TV): a governança nunca vê nome; a recepção
// pede o nome ao clicar num quarto, por hospedes_do_quarto (0023) — o
// banco só entrega a quem tem hóspedes: ver.
//
// A regra (estado do quarto, o que é alerta, a hora do hotel) mora no
// banco (painel_tempo_real). A tela chama a função a cada 20 s (lib/painel:
// mais devagar com a aba escondida, com recuo quando a rede falha) e
// redesenha inteira: o json é pequeno e a TV não tem estado próprio além
// do quarto selecionado.
//
// Modo TV (#painel?tv=1): sem menu, sem cabeçalho, a página inteira escala
// para caber em 16:9 sem rolagem; o botão pede tela cheia ao navegador.
// =====================================================================
import { el, icone } from '../ui.js';
import {
  validarPainel, indices, classeDoQuarto, marcasDoQuarto, resumoDoAndar, colunasDaGrade, contagemPorClasse, CLASSES_LEGENDA, CLASSES_RESUMO, ROTULO_CLASSE,
  chegadasSemQuarto, chipsDoAlerta, textoDoAlerta, medidores, prioridadeGovernanca, chegadasPorFaixa, detalheDoQuarto, linhasDeHospedes,
  vestirMovimento, vestirAlertas, estaVelha, proximoIntervalo, rotuloDaHora, rotuloDoDia, parametrosDoHash, vistaPadrao, hashDoPainel, limitar,
  MAX_ALERTAS, MAX_MOVIMENTO, MAX_SAIDAS, DETALHE_MS,
} from '../lib/painel.js';
import { hora } from '../lib/governanca.js';

export function montar(cont, { auth, contexto, agora = () => Date.now(), documento = document, janela = window }) {
  const sistema = !!(contexto.usuario && contexto.usuario.sistema);
  const nivelDe = ch => ((contexto.modulos || []).find(m => m.chave === ch) || {}).nivel;
  const veHospedes = sistema || nivelDe('hospedes') === 'ver' || nivelDe('hospedes') === 'editar';
  let vivo = true, dados = null, clientes = [], falhas = 0, ultimaResposta = 0, ultimoErro = '', timer = null;
  let clienteSel = sistema ? null : contexto.cliente.id;
  const { tv: modoTv, vista } = parametrosDoHash(janela.location.hash, vistaPadrao(contexto));
  const pc = () => (sistema ? { p_cliente: clienteSel } : {});
  // o quarto selecionado e o que o banco disse sobre quem está nele
  let selecionado = null, hospedes = null, hospedesErro = '', timerDetalhe = null;

  async function carregar() {
    if (sistema && !clientes.length) { clientes = await auth.ler('clientes', 'select=id,slug,nome&order=nome'); if (!clienteSel && clientes.length) clienteSel = clientes[0].id; }
    if (!clienteSel) { dados = null; return; }
    const j = await auth.rpc('painel_tempo_real', pc());
    const erros = validarPainel(j);
    if (erros.length) throw new Error('Resposta inesperada do servidor: ' + erros.join(', '));
    dados = j; ultimaResposta = agora(); ultimoErro = ''; falhas = 0;
  }
  async function ciclo() {
    if (!vivo) return;
    try { await carregar(); } catch (e) { falhas++; ultimoErro = e.message || String(e); }
    desenhar();
    agendar();
  }
  function agendar() {
    if (!vivo) return;
    clearTimeout(timer);
    timer = setTimeout(ciclo, proximoIntervalo({ escondida: documento.visibilityState === 'hidden', falhas }));
  }
  const aoVoltar = () => { if (documento.visibilityState === 'visible') { clearTimeout(timer); ciclo(); } };
  documento.addEventListener('visibilitychange', aoVoltar);
  const aoTeclar = ev => { if (ev.key === 'Escape' && selecionado) limparSelecao(); };
  documento.addEventListener('keydown', aoTeclar);

  async function selecionar(numero) {
    selecionado = numero; hospedes = null; hospedesErro = '';
    clearTimeout(timerDetalhe);
    timerDetalhe = setTimeout(limparSelecao, DETALHE_MS);
    desenhar();
    // só a recepção pergunta quem está no quarto — e só se este usuário vê hóspedes
    const q = dados && indices(dados).porNumero.get(numero);
    if (vista === 'recepcao' && veHospedes && q && (q.ocupado || q.chega_hoje)) {
      try {
        const lista = await auth.rpc('hospedes_do_quarto', { p_numero: numero, ...pc() });
        if (selecionado === numero) { hospedes = Array.isArray(lista) ? lista : []; desenhar(); }
      } catch (e) { if (selecionado === numero) { hospedesErro = e.message || String(e); desenhar(); } }
    }
  }
  function limparSelecao() { clearTimeout(timerDetalhe); selecionado = null; hospedes = null; hospedesErro = ''; desenhar(); }

  function desenhar() {
    if (!vivo) return;
    cont.replaceChildren();
    cont.dataset.tela = 'painel';
    cont.dataset.vista = vista;
    documento.body.classList.toggle('modo-tv', modoTv);
    const velha = estaVelha(ultimaResposta, agora());
    const raiz = el('div', { class: 'pn', id: 'pn', dataset: { vista } });
    cont.appendChild(raiz);
    // ── topo ──
    raiz.appendChild(el('div', { class: 'pn-topo' },
      el('div', { class: 'pn-marca' },
        el('img', { src: 'logo.png', alt: '' }),
        el('div', {},
          el('h1', { class: 'pn-h1' }, 'Liberação de Quartos'),
          el('div', { class: 'pn-sub', id: 'pn-cliente' }, dados
            ? `${dados.cliente} · ${(dados.contadores || {}).quartos || 0} UHs · check-in ${dados.hora_checkin} · check-out ${dados.hora_checkout} · atualizado ${rotuloDaHora(dados)}`
            : (sistema ? 'Painel' : contexto.cliente.nome)))),
      el('div', { class: 'pn-vista', id: 'pn-vista' },
        el('b', {}, vista === 'recepcao' ? 'Recepção' : 'Governança'),
        el('i', {}, vista === 'recepcao' ? (veHospedes ? 'clique num quarto para ver quem chega' : 'este usuário não vê hóspedes') : 'a TV da governança nunca vê nomes'),
        velha || ultimoErro ? el('span', { class: 'badge badge-red', id: 'pn-velha', title: ultimoErro }, velha ? `sem atualizar desde ${hora(new Date(ultimaResposta).toISOString())}` : 'falha ao atualizar — tentando de novo') : null),
      el('div', { class: 'pn-acoes' },
        sistema ? el('select', { class: 'input', id: 'pn-cliente-sel', style: { width: 'auto' }, onchange: ev => { clienteSel = ev.target.value; dados = null; limparSelecao(); clearTimeout(timer); ciclo(); } },
          clientes.map(c => el('option', { value: c.id, selected: c.id === clienteSel }, c.nome))) : null,
        el('a', { class: 'btn btn-secondary btn-sm', id: 'pn-outra-vista', href: hashDoPainel({ tv: modoTv, vista: vista === 'recepcao' ? 'governanca' : 'recepcao' }) }, vista === 'recepcao' ? 'Vista da governança' : 'Vista da recepção'),
        el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'pn-tv', onclick: alternarModoTv }, icone('ic-dashboard'), modoTv ? 'Sair do modo TV' : 'Modo TV')),
      el('div', { class: 'pn-relogio' }, el('div', { class: 'pn-hora', id: 'pn-hora' }, rotuloDaHora(dados)), el('div', { class: 'pn-dia', id: 'pn-dia' }, rotuloDoDia(dados)))));
    if (!dados) {
      raiz.appendChild(el('p', { class: 'vazio', id: 'pn-vazio' }, ultimoErro ? `Não foi possível carregar o painel: ${ultimoErro}` : 'Carregando…'));
      return;
    }
    const ix = indices(dados);
    // ── alerta grande + medidores ──
    const sem = chegadasSemQuarto(dados);
    const chips = chipsDoAlerta(dados, vista);
    raiz.appendChild(el('div', { class: 'pn-faixa' },
      el('div', { class: 'pn-alerta-grande' + (sem.length ? '' : ' ok'), id: 'pn-alerta-grande', dataset: { n: String(sem.length) } },
        el('div', { class: 'n' }, String(sem.length)),
        el('div', { class: 'txt' }, textoDoAlerta(sem.length)),
        el('div', { class: 'chips' + (chips.length > 3 ? ' duas' : ''), id: 'pn-chips' }, chips.map(c =>
          el('span', { class: 'chip', dataset: { chave: c.chave } }, el('i', { class: 'cor-' + c.chave }), c.rotulo, el('b', {}, String(c.n)))))),
      el('div', { class: 'pn-medidores', id: 'pn-medidores' }, medidores(dados).map(m =>
        el('div', { class: 'pn-med', dataset: { chave: m.chave, pct: String(m.pct) } }, anel(m),
          el('div', {}, el('div', { class: 'lbl' }, m.rotulo), el('div', { class: 'meta' }, m.meta)))))));
    // ── legenda ──
    const cont1 = contagemPorClasse(ix.quartos);
    raiz.appendChild(el('div', { class: 'pn-legenda', id: 'pn-legenda' },
      CLASSES_LEGENDA.map(c => el('span', { class: 'leg' }, el('s', { class: 'cor-' + c }), ROTULO_CLASSE[c], ' ', el('span', { class: 'qtd' }, String(cont1[c] || 0)))),
      el('span', { class: 'leg' }, el('s', { class: 'cor-ocupado marca-chega' }), 'ponto = chega hoje (verde: já chegou)'),
      el('span', { class: 'leg' }, el('s', { class: 'cor-a_limpar marca-cedo' }), 'borda laranja = chega cedo'),
      el('span', { class: 'leg' }, el('s', { class: 'cor-aguardando_inspecao marca-urgente' }), 'borda vermelha = alerta')));
    // ── grade ──
    const grade = el('div', { class: 'pn-grade', id: 'pn-grade' });
    grade.style.setProperty('--pn-colunas', String(colunasDaGrade(dados.andares)));
    grade.style.setProperty('--pn-andares', String(dados.andares.length || 1));
    for (const a of dados.andares) {
      const r = resumoDoAndar(a.quartos);
      grade.appendChild(el('section', { class: 'pn-andar', dataset: { andar: a.andar === null || a.andar === undefined ? '' : String(a.andar) } },
        el('div', { class: 'pn-andar-lbl' }, a.andar === null || a.andar === undefined ? '—' : `${a.andar}º`),
        el('div', { class: 'pn-uhs' }, (a.quartos || []).map(q => {
          const classe = classeDoQuarto(q), marcas = marcasDoQuarto(q, ix);
          return el('button', { type: 'button', class: ['pn-uh', 'cor-' + classe, ...marcas].join(' '), dataset: { quarto: q.numero, estado: q.estado, classe, cedo: q.chega_cedo ? '1' : '' },
            'aria-pressed': selecionado === q.numero ? 'true' : 'false', title: `${q.numero} — ${ROTULO_CLASSE[classe]}`, onclick: () => selecionar(q.numero) }, q.numero);
        })),
        el('div', { class: 'pn-andar-num' }, CLASSES_RESUMO.map(c => el('span', { class: 'bx cor-' + c, title: ROTULO_CLASSE[c], dataset: { classe: c } }, String(r[c]))))));
    }
    raiz.appendChild(grade);
    // ── baixo: esquerda (prioridade / chegadas / detalhe) e direita (alertas / saídas) ──
    raiz.appendChild(el('div', { class: 'pn-baixo' }, painelEsquerdo(ix), painelDireito(ix)));
  }

  function painelEsquerdo(ix) {
    if (selecionado) {
      const d = detalheDoQuarto(dados, selecionado, ix);
      if (d) return el('div', { class: 'pn-painel', id: 'pn-detalhe', dataset: { quarto: d.numero } },
        el('h2', {}, 'Detalhe da UH', el('button', { type: 'button', class: 'x', id: 'pn-fechar', onclick: limparSelecao }, 'voltar ✕')),
        el('div', { class: 'pn-det-top' }, el('span', { class: 'num' }, d.numero), el('span', { class: 'pill cor-' + d.classe }, d.rotulo)),
        el('dl', { class: 'pn-linhas' }, d.linhas.flatMap(([dt, dd, oculto]) => [el('dt', {}, dt), el('dd', { class: oculto ? 'oculto' : '' }, dd)]),
          d.temHospede ? el('dt', {}, 'Hóspede') : null, d.temHospede ? el('dd', { id: 'pn-hospede' }, linhasDoHospede()) : null),
        el('div', { class: 'pn-nota' }, vista === 'recepcao'
          ? 'A recepção vê o nome para receber o hóspede (consulta à ficha, só para quem tem Hóspedes: ver). Valor da reserva não aparece em nenhuma vista.'
          : 'A TV da governança vê a casa inteira para distribuir o trabalho, mas nunca dados do hóspede. O detalhe some sozinho em 20 s.'));
    }
    if (vista === 'recepcao') {
      return el('div', { class: 'pn-painel', id: 'pn-chegadas' },
        el('h2', {}, 'Chegadas de hoje, por hora prevista'),
        el('div', { class: 'pn-prio' }, chegadasPorFaixa(dados).map(l => linhaDePrioridade(l, l.chave === 'chegaram' ? 'liberado' : 'ocupado', it => el('span', { class: it.pronto ? 'pronto' : 'sem-quarto' }, it.numero)))),
        el('div', { class: 'pn-nota' }, 'verde = quarto liberado · vermelho = ainda sem quarto. Clique num quarto para ver quem chega.'));
    }
    return el('div', { class: 'pn-painel', id: 'pn-prioridade' },
      el('h2', {}, 'Prioridade agora'),
      el('div', { class: 'pn-prio' }, prioridadeGovernanca(dados, ix).map(l => linhaDePrioridade(l, l.chave, it => el('span', { class: it.lento ? 'lento' : '' }, it.numero, it.extra ? el('em', {}, ' ' + it.extra) : null)))));
  }
  function linhaDePrioridade(l, cor, desenhaUm) {
    const itens = [];
    l.mostrar.forEach((it, i) => { if (i) itens.push(' · '); itens.push(desenhaUm(it)); });
    return el('div', { class: 'pn-prio-linha', dataset: { chave: l.chave, n: String(l.n) } },
      el('span', { class: 'pn-prio-tag cor-' + cor }, `${l.rotulo} ${l.n}`),
      el('span', { class: 'pn-prio-uhs' }, l.n ? itens : el('em', {}, 'nada pendente'), l.resto ? el('span', { class: 'mais' }, ` +${l.resto}`) : null));
  }
  function linhasDoHospede() {
    if (vista !== 'recepcao') return el('span', { class: 'oculto' }, 'oculto nesta vista');
    if (!veHospedes) return el('span', { class: 'oculto' }, 'este usuário não vê hóspedes');
    if (hospedesErro) return el('span', { class: 'oculto' }, hospedesErro);
    if (hospedes === null) return el('span', { class: 'oculto' }, 'consultando…');
    if (!hospedes.length) return el('span', { class: 'oculto' }, 'ninguém na ficha de hoje');
    return linhasDeHospedes(hospedes).map(h => el('div', { class: 'pn-hospede' }, h.nome, h.nota ? el('span', { class: 'oculto' }, ` (${h.nota})`) : null));
  }
  function painelDireito(ix) {
    if (vista === 'recepcao') {
      const pend = ((dados.saidas || {}).lista || []).filter(s => !s.saiu), lp = limitar(pend, MAX_SAIDAS);
      return el('div', { class: 'pn-painel', id: 'pn-saidas' },
        el('h2', {}, 'Saídas de hoje'),
        el('div', { class: 'pn-evs' },
          el('div', { class: 'ev' }, el('span', { class: 'h' }, String((dados.saidas || {}).sairam || 0)), el('span', { class: 'c' }, 'já saíram')),
          el('div', { class: 'ev' }, el('span', { class: 'h' }, String(pend.length)), el('span', { class: 'c' }, 'ainda dentro · check-out até ', el('b', {}, dados.hora_checkout))),
          el('div', { class: 'ev' }, el('span', { class: 'c lista', id: 'pn-saidas-lista' }, lp.itens.map(s => s.numero).join(' · '), lp.resto ? el('span', { class: 'mais' }, ` +${lp.resto}`) : null)),
          el('div', { class: 'ev' }, el('span', { class: 'h' }, String((dados.chegadas || {}).cedo || 0)), el('span', { class: 'c' }, 'chegam ', el('b', {}, `antes das ${dados.hora_checkin}`), ' — quartos com borda laranja na grade'))));
    }
    const al = vestirAlertas(dados.alertas), la = limitar(al, MAX_ALERTAS);
    const mv = vestirMovimento(dados.movimento), lm = limitar(mv, MAX_MOVIMENTO);
    return el('div', { class: 'pn-lado' },
      el('div', { class: 'pn-painel', id: 'pn-alertas' },
        el('h2', {}, `Alertas agora (${al.length})`),
        el('div', { class: 'pn-evs' },
          ...la.itens.map(a => el('div', { class: 'ev pn-alerta ' + a.classe, dataset: { tipo: a.tipo, quarto: a.numero } }, el('span', { class: 'h' }, a.rotulo), el('span', { class: 'c' }, a.texto), a.dur ? el('span', { class: 'dur' }, a.dur) : null)),
          la.resto ? el('div', { class: 'ev' }, el('span', { class: 'c mais', id: 'pn-alertas-mais' }, `+ ${la.resto} alertas — os quartos estão marcados na grade`)) : null,
          al.length ? null : el('div', { class: 'ev' }, el('span', { class: 'c mais' }, 'nenhum alerta agora')))),
      el('div', { class: 'pn-painel', id: 'pn-movimento' },
        el('h2', {}, 'Movimento ao vivo'),
        el('div', { class: 'pn-evs' },
          ...lm.itens.map(m => el('div', { class: 'ev' }, el('span', { class: 'h' }, m.hora), el('span', { class: 'c' }, m.texto), m.dur ? el('span', { class: 'dur' }, m.dur) : null)),
          mv.length ? null : el('div', { class: 'ev' }, el('span', { class: 'c mais' }, 'nada aconteceu ainda hoje')))));
  }
  // o anel do medidor: SVG puro, sem biblioteca
  function anel(m) {
    const R = 31, C = 2 * Math.PI * R, f = m.v;
    const svg = documento.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 78 78'); svg.setAttribute('role', 'img'); svg.setAttribute('aria-label', `${m.pct} por cento`);
    const fundo = documento.createElementNS('http://www.w3.org/2000/svg', 'circle');
    for (const [k, v] of Object.entries({ cx: 39, cy: 39, r: R, fill: 'none', class: 'fundo', 'stroke-width': 8 })) fundo.setAttribute(k, v);
    const arco = documento.createElementNS('http://www.w3.org/2000/svg', 'circle');
    for (const [k, v] of Object.entries({ cx: 39, cy: 39, r: R, fill: 'none', class: 'arco ' + m.chave, 'stroke-width': 8, 'stroke-linecap': 'round', 'stroke-dasharray': `${(C * f).toFixed(1)} ${(C * (1 - f) + 2).toFixed(1)}`, transform: 'rotate(-90 39 39)' })) arco.setAttribute(k, v);
    const num = documento.createElementNS('http://www.w3.org/2000/svg', 'text');
    for (const [k, v] of Object.entries({ x: 39, y: 42, 'text-anchor': 'middle', class: 'ringnum' })) num.setAttribute(k, v);
    num.textContent = String(m.pct);
    const pct = documento.createElementNS('http://www.w3.org/2000/svg', 'text');
    for (const [k, v] of Object.entries({ x: 39, y: 55, 'text-anchor': 'middle', class: 'ringpct' })) pct.setAttribute(k, v);
    pct.textContent = '%';
    svg.append(fundo, arco, num, pct);
    return svg;
  }

  function alternarModoTv() {
    const ligar = !modoTv;
    if (ligar && documento.documentElement.requestFullscreen) documento.documentElement.requestFullscreen().catch(() => {});
    if (!ligar && documento.fullscreenElement && documento.exitFullscreen) documento.exitFullscreen().catch(() => {});
    janela.location.hash = hashDoPainel({ tv: ligar, vista });
  }

  desenhar();
  ciclo();
  return () => {
    vivo = false; clearTimeout(timer); clearTimeout(timerDetalhe);
    documento.removeEventListener('visibilitychange', aoVoltar);
    documento.removeEventListener('keydown', aoTeclar);
    documento.body.classList.remove('modo-tv');
  };
}
